import { isArray, isNumber, isObject, last } from '@config/utils';
import Proj4js from 'proj4';
// import {get as getProjection} from 'ol/proj';

export const getCompatibleSRS = (srs, allowedSRS) => {
    if (srs === 'EPSG:900913' && !allowedSRS['EPSG:900913'] && allowedSRS['EPSG:3857']) {
        return 'EPSG:3857';
    }
    if (srs === 'EPSG:3857' && !allowedSRS['EPSG:3857'] && allowedSRS['EPSG:900913']) {
        return 'EPSG:900913';
    }
    if (srs === 'CRS:84' && !allowedSRS['CRS:84'] && allowedSRS['EPSG:4326']) {
        return 'EPSG:4326';
    }
    return srs;
};
export const getEquivalentSRS = (srs) => {
    if (srs === 'EPSG:900913' || srs === 'EPSG:3857') {
        return ['EPSG:3857', 'EPSG:900913'];
    }
    return [srs];
};
export const getEPSGCode = (code) => {
    if (code.indexOf(':') !== -1) {
        return 'EPSG:' + code.substring(code.lastIndexOf(':') + 1);
    }
    return code;
};
export const normalizeSRS = function(srs, allowedSRS) {
    const result = srs === 'EPSG:900913' ? 'EPSG:3857' : (srs === 'CRS:84' ? 'EPSG:4326' : srs);
    if (allowedSRS && !allowedSRS[result]) {
        return CoordinatesUtils.getCompatibleSRS(result, allowedSRS);
    }
    return result;
};
export const isAllowedSRS = (srs, allowedSRS) => {
    return allowedSRS[CoordinatesUtils.getCompatibleSRS(srs, allowedSRS)];
};
export const getAvailableCRS = function() {
    let crsList = {};
    for (let a in Proj4js.defs) {
        if (Proj4js.defs.hasOwnProperty(a)) {
            crsList[a] = {label: crsLabels[a] || a};
        }
    }
    return crsList;
};
export const normalizePoint = (point) => {
    return {
        x: point.x || 0.0,
        y: point.y || 0.0,
        srs: point.srs || point.crs || 'EPSG:4326',
        crs: point.srs || point.crs || 'EPSG:4326'
    };
};
const numberize = (point) => {
    let outpoint = point;
    if (!isNumber(point.x)) {
        outpoint.x = parseFloat(point.x);
    }
    if (!isNumber(point.y)) {
        outpoint.y = parseFloat(point.y);
    }
    return outpoint;
};
export const reproject = (point, source, dest, normalize = true) => {
    const sourceProj = source && Proj4js.defs(source) ? new Proj4js.Proj(source) : null;
    const destProj = dest && Proj4js.defs(dest) ? new Proj4js.Proj(dest) : null;
    if (sourceProj && destProj) {
        let p = isArray(point) ? Proj4js.toPoint(point) : Proj4js.toPoint([point.x, point.y]);

        const transformed = Object.assign({}, source === dest ? numberize(p) : Proj4js.transform(sourceProj, destProj, numberize(p)), {srs: dest});
        if (normalize) {
            return normalizePoint(transformed);
        }
        return transformed;
    }
    return null;
};
export const normalizeLng = (lng) => {
    let tLng = lng / 360 % 1 * 360;
    if (tLng < -180) {
        tLng = tLng + 360;
    } else if (tLng > 180) {
        tLng = tLng - 360;
    }
    return tLng;
};
/**
 * Parse the URN to get EPSG code
 * @param {object|string} crs object or string
 * @return {string} EPSG in the form EPSG:NNNN
 */
export const parseURN = (crs) => {
    const code = crs && crs.properties && crs.properties.name || crs && crs.name || crs && crs.properties && crs.properties.code || crs;

    let crsCode = code && last(code.split(":"));

    if (crsCode === "WGS 1984" || crsCode === "WGS84") {
        return "EPSG:4326";
    } else if (crsCode) {
        // TODO check is valid EPSG code
        return "EPSG:" + crsCode;
    }
    return null;
};

/**
 * Reprojects a bounding box.
 * @param bbox {array} [minx, miny, maxx, maxy]
 * @param source {string} SRS of the given bbox
 * @param dest {string} SRS of the returned bbox
 * @return {array} [minx, miny, maxx, maxy]
 */
export const reprojectBbox = function(bbox, source, dest, normalize = true) {
    let points;
    if (isArray(bbox)) {
        points = {
            sw: [bbox[0], bbox[1]],
            ne: [bbox[2], bbox[3]]
        };
    } else {
        points = {
            sw: [bbox.minx, bbox.miny],
            ne: [bbox.maxx, bbox.maxy]
        };
    }
    let projPoints = [];
    for (let p in points) {
        if (points.hasOwnProperty(p)) {
            const projected = CoordinatesUtils.reproject(points[p], source, dest, normalize);
            if (projected) {
                let {x, y} = projected;
                projPoints.push(x);
                projPoints.push(y);
            } else {
                return null;
            }
        }
    }
    return projPoints;
};
export const getWMSBoundingBox = (BoundingBox, mapSRS) => {
    const SRS = mapSRS || 'EPSG:3857';
    const bbox = BoundingBox && isArray(BoundingBox) && head(BoundingBox.filter(b => {
        return b && b.$ && b.$.SRS === SRS && b.$.maxx && b.$.maxy && b.$.minx && b.$.miny;
    }).map(b => b && b.$ && CoordinatesUtils.reprojectBbox([
        parseFloat(b.$.minx),
        parseFloat(b.$.miny),
        parseFloat(b.$.maxx),
        parseFloat(b.$.maxy)
    ], SRS, 'EPSG:4326')));
    return isArray(bbox) && {minx: bbox[0], miny: bbox[1], maxx: bbox[2], maxy: bbox[3]} || null;
};
export const isSRSAllowed = (srs) => {
    return !!Proj4js.defs(srs);
};

/**
 * Method: createCRSObject
 * Create the CRS object for an object.
 *
 * Parameters:
 * object - {<ol.proj.Projection>}
 *
 * Returns:
 * {Object} An object which can be assigned to the crs property
 * of a GeoJSON object.
 */
export const createCRSObject = (proj) => {
    proj = isObject(proj) ? proj.getCode() : proj;
    let crs = {};
    if (proj.match(/epsg:/i)) {
        var code = parseInt(proj.substring(proj.indexOf(":") + 1));
        if (code == 4326) {
            crs = {
                "type": "name",
                "properties": {
                    "name": "urn:ogc:def:crs:OGC:1.3:CRS84"
                }
            };
        } else {
            crs = {
                "type": "name",
                "properties": {
                    "name": "urn:ogc:def:crs:EPSG::" + code
                }
            };
        }
    }
    return crs;
};

export const getUnits = function(projection) {
    const proj = new Proj4js.Proj(projection);
    return proj.units || 'degrees';
};

let CoordinatesUtils = {
    reproject,
    normalizePoint,
    normalizeLng,
    reprojectBbox,
    getCompatibleSRS,
    getEquivalentSRS,
    getEPSGCode,
    normalizeSRS,
    isAllowedSRS,
    getAvailableCRS,
    getWMSBoundingBox,
    isSRSAllowed,
};

export default CoordinatesUtils;